// ABOUTME: Shared row-list renderer for the in-app consent categories (no voice row under mic_voice_control, dev + staging).
// ABOUTME: One `theme` ("reel" | "generic") covers every surface that lists these rows.

import React from 'react';
import { Check } from 'lucide-react';
import { CATEGORIES, PANEL_CATEGORIES } from './consentConfig';
import { useMicVoiceControl } from './micVoiceControl';

/**
 * Renders the checkbox + label + "always on" badge + description row for every category.
 * Kept as the single place this markup exists — the rail's ConsentPanel, the menu's MenuConsentPanel and the
 * non-reel drawer all render the same rows (the pre-entry ConsentGate stopped rendering rows on 2026-10-08) through this component so the copy and accessibility
 * contract (native `<input type="checkbox" aria-label={cat.label}>`) never drifts between
 * surfaces.
 *
 * The reel theme takes `unit`/`geometry` rather than Tailwind breakpoints: it renders inside the
 * `--u`-scaled rail (see railGeometry.js/mobileGeometry.js), which is a continuous clamp() across
 * every viewport width, not a set of fixed breakpoints — mixing the two would fight the rail's own
 * scaling. Callers pass `u`/`CONSENT_PANEL` (desktop) or `mu`/`MOBILE_CONSENT` (mobile).
 *
 * @param {{
 *   selected: Record<string, boolean>,
 *   onToggle: (id: string) => void,
 *   theme?: 'reel' | 'generic',
 *   unit?: (authoredPx: number) => string,
 *   geometry?: { rowH: number, rowGap: number, rowRadius: number, rowIconSize: number },
 *   text?: { rowTitle: number, rowBody: number },
 *   type?: (authoredPx: number) => string,  // type sizer with a real-px floor; defaults to `unit`
 * }} props
 */
export function ConsentCategoryList({ selected, onToggle, theme = 'generic', unit, geometry, text, type }) {
  const rows = useMicVoiceControl() ? PANEL_CATEGORIES : CATEGORIES; // mic_voice_control: the mic button owns voice
  if (theme === 'reel') {
    const u = unit;
    const g = geometry;
    const t = text;
    const font = type || u;
    return (
      <div className="flex flex-col" style={{ gap: u(g.rowGap) }}>
        {/* A <label> — the same element the generic theme already uses — so the whole row toggles
            the category, not just the 48-authored-px box (20px on a desktop, 15px on a phone) it
            used to be. No visual change: the row's box, blur and radius are untouched (2026-09-03). */}
        {rows.map((cat) => (
          <label
            key={cat.id}
            className={`flex items-start bg-reel-chrome-panel backdrop-blur-reel-panelDense ${cat.locked ? 'opacity-50' : 'cursor-pointer'}`}
            style={{
              minHeight: u(g.rowH), borderRadius: u(g.rowRadius), padding: u(40), gap: u(50.667),
              // Same fix as PointTag.js/CaptionOverlay.js's backdrop-blur surfaces: without an
              // explicit compositing layer, this row's blur (nested inside the panel's OWN
              // backdrop-blur) rendered with a visible seam of two different background tones along
              // one edge (reported 2026-08-28) — a backdrop-filter compositing artifact, not a color
              // actually being drawn twice.
              transform: 'translateZ(0)',
              willChange: 'backdrop-filter, transform',
              contain: 'paint',
            }}
          >
            <span className="relative inline-flex shrink-0" style={{ width: u(g.rowIconSize), height: u(g.rowIconSize) }}>
              <input
                type="checkbox"
                aria-label={cat.label}
                checked={!!selected[cat.id]}
                disabled={cat.locked}
                onChange={() => onToggle(cat.id)}
                className="peer absolute inset-0 appearance-none bg-black cursor-pointer disabled:cursor-default"
                style={{ borderRadius: u(10) }}
              />
              <Check className="pointer-events-none absolute inset-0 m-auto text-white opacity-0 peer-checked:opacity-100" style={{ width: u(28), height: u(28) }} />
            </span>
            <span className="flex flex-col" style={{ gap: u(8) }}>
              <span className="font-reel-heading font-bold text-reel-chrome-textPrimary" style={{ fontSize: font(t.rowTitle) }}>
                {cat.label}
                {cat.locked && (
                  <span className="ml-3 font-reel-body font-normal text-reel-chrome-textDim uppercase tracking-widest align-middle" style={{ fontSize: font(t.rowBody) }}>
                    Always on
                  </span>
                )}
              </span>
              <span className="font-reel-body text-reel-chrome-textDim leading-relaxed" style={{ fontSize: font(t.rowBody) }}>
                {cat.description}
              </span>
            </span>
          </label>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {rows.map((cat) => (
        <label
          key={cat.id}
          className={`flex gap-3 items-start border border-black/10 rounded-2xl p-4 ${
            cat.locked ? 'bg-black/[0.03]' : 'cursor-pointer hover:bg-black/[0.02]'
          }`}
        >
          <input
            type="checkbox"
            aria-label={cat.label}
            checked={!!selected[cat.id]}
            disabled={cat.locked}
            onChange={() => onToggle(cat.id)}
            className="mt-1 h-4 w-4 shrink-0 accent-black"
          />
          <span className="flex flex-col">
            <span className="text-sm font-medium tracking-wide">
              {cat.label}
              {cat.locked && (
                <span className="ml-2 text-[10px] uppercase tracking-widest text-neutral-400">always on</span>
              )}
            </span>
            <span className="text-xs text-neutral-500 leading-relaxed mt-1">{cat.description}</span>
          </span>
        </label>
      ))}
    </div>
  );
}
